import styled from '@emotion/styled';
import { Button, Drawer, Input, Space, Table, Tag } from 'antd';
import moment from 'moment';
import { ReactNode, useEffect, useState } from 'react';
import { FaEyeSlash, FaKey, FaShieldAlt, FaUserCheck, FaUsers } from 'react-icons/fa';
import { FiSearch } from 'react-icons/fi';
import { MdClose } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { PulseLoader } from 'react-spinners';
import { ConnectedUserEntity } from '../../../../entities/ConnectedUserEntity';
import { UserEntity } from '../../../../entities/utilisateur.entity';
import { PRIMARY, YELLOW } from '../../../../shared/colors';
import { Layout } from '../../../shared/Layout';
import { PasswordForm } from '../components/PasswordForm';
import { getUtilisateurDetails } from '../network';

const Container = styled.div`
  min-height: calc(100vh - 76px);
  background:
    radial-gradient(circle at 8% 0%, rgba(255, 79, 31, 0.08), transparent 24%),
    radial-gradient(circle at 92% 8%, rgba(255, 176, 0, 0.08), transparent 22%),
    var(--mt-bg);
  color: var(--mt-text);
  font-family: Poppins-R;

  .bread-crum span {
    color: var(--mt-text);
  }

  td.ant-table-column-sort {
    background-color: transparent !important;
  }

  th {
    background-color: ${PRIMARY} !important;
    color: #fff7e8 !important;
    font-weight: bold;
  }

  .odd-row {
    background-color: var(--mt-row-odd);
    color: var(--mt-text);
  }

  .even-row {
    background-color: var(--mt-row-even);
    color: var(--mt-text);
  }

  .odd-row,
  .even-row {
    &:hover > td {
      background-color: var(--mt-row-hover) !important;
      cursor: pointer;
      color: var(--mt-text);
    }
  }

  .content {
    padding: 12px;
    width: 100%;
    background: transparent;
    color: var(--mt-text);
  }

  .admin-hero {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 18px;
    background:
      linear-gradient(135deg, rgba(255, 79, 31, 0.13), transparent 38%),
      ${PRIMARY};
    box-shadow: var(--mt-card-shadow);
  }

  .eyebrow {
    color: ${YELLOW};
    font-family: Poppins-B;
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .admin-hero h1 {
    margin: 6px 0 0;
    color: #fff7e8;
    font-family: Poppins-B;
    font-size: 30px;
  }

  .admin-hero p {
    max-width: 760px;
    margin: 8px 0 0;
    color: rgba(255, 247, 232, 0.72);
  }

  .hero-icon {
    color: var(--mt-warning);
  }

  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
    margin-top: 12px;
  }

  .kpi-card,
  .page-toolbar,
  .table-shell {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    background: var(--mt-card);
    box-shadow: var(--mt-card-shadow);
  }

  .kpi-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 78px;
    padding: 13px;
  }

  .kpi-card .label {
    color: var(--mt-muted);
    font-size: 11px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .kpi-card .value {
    display: block;
    margin-top: 5px;
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 22px;
  }

  .kpi-card .icon {
    color: var(--mt-warning);
  }

  .page-toolbar {
    display: grid;
    grid-template-columns: minmax(260px, 360px) 1fr auto;
    align-items: center;
    gap: 12px;
    margin: 12px 0;
    padding: 12px;
  }

  .search-box {
    position: relative;
  }

  .search-box svg {
    position: absolute;
    z-index: 1;
    top: 50%;
    left: 11px;
    color: var(--mt-muted);
    transform: translateY(-50%);
  }

  .search-box .ant-input {
    height: 38px;
    border-color: var(--mt-border);
    border-radius: 999px;
    padding-left: 34px;
    background: var(--mt-surface);
    color: var(--mt-text);
  }

  .table-shell {
    overflow: hidden;
    padding: 12px;
  }

  .action-button {
    display: inline-grid;
    place-items: center;
    width: 30px;
    height: 30px;
    border: 1px solid var(--mt-border);
    border-radius: 999px;
    background: var(--mt-surface);
    color: var(--mt-text);
  }

  @media (max-width: 760px) {
    .content {
      padding: 18px 12px;
    }

    .admin-hero,
    .page-toolbar {
      grid-template-columns: 1fr;
      flex-direction: column;
      align-items: flex-start;
    }

    .kpi-grid {
      grid-template-columns: 1fr;
    }
  }
`;

export const GestionPasswordPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [showDrawer, setShowDrawer] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [filterUsers, setFilterUsers] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserEntity>();
  const connectedUser: ConnectedUserEntity = useSelector(
    (state: any) => state.userReducer,
  ).user;

  const fetchData = () => {
    setIsLoading(true);
    getUtilisateurDetails(connectedUser.id).then(({ data }) => {
      setUsers(data);
      setFilterUsers(data);
      setIsLoading(false);
    });
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setColor = (code: string) => {
    switch (code) {
      case 'admin':
        return '#f50';
      case 'resp_commercial':
        return '#3c096c';
      case 'commercial':
        return '#f9a825';
      case 'responsable':
        return '#108ee9';
      case 'chef_agence':
        return '#8d6e63';
      case 'gestionnaire':
        return '#5f0f40';
      case 'sadmin':
        return '#fb8b24';
    }
  };

  const COLUMNS = [
    {
      title: 'Nom',
      dataIndex: 'nom',
      flex: 1,
    },
    {
      title: 'Prénom',
      dataIndex: 'prenom',
      flex: 1,
    },
    {
      title: 'Adresse',
      dataIndex: 'adresse',
      flex: 1,
    },
    {
      title: 'Agence',
      render: (cell: any, row: any) => row.agence?.nom,
      flex: 1,
    },
    {
      title: 'Partenaire',
      render: (cell: any, row: any) => row.partner?.nom,
      flex: 1,
    },
    {
      title: 'Date création',
      dataIndex: 'created_at',
      render: (cell: any) => `${moment(cell).format('DD/MM/YYYY')}`,
      flex: 1,
    },
    {
      title: 'Date modification',
      dataIndex: 'updated_at',
      render: (cell: any) => `${moment(cell).format('DD/MM/YYYY')}`,
      flex: 1,
    },
    {
      title: 'Profils',
      render: (cell: any, row: any) => {
        return (
          <Space>
            {row.profils.length === 0 ? (
              <Tag>--</Tag>
            ) : (
              row.profils.map((pro: any) => (
                <Tag key={pro.id} color={setColor(pro.code_profil)}>
                  {pro.nom}
                </Tag>
              ))
            )}
          </Space>
        );
      },
      flex: 1,
    },
    {
      title: 'Statut',
      render: (cell: any, row: any) => (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          {row.actif ? (
            <div
              style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                backgroundColor: 'green',
              }}
            ></div>
          ) : (
            <div
              style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                backgroundColor: 'red',
              }}
            ></div>
          )}
        </div>
      ),
      flex: 1,
    },
    {
      title: 'Action',
      render: (cell: any, row: any) => (
        <Space>
          <Button
            className='action-button'
            icon={<FaEyeSlash />}
            onClick={() => {
              setSelectedUser(row);
              setShowDrawer(true);
            }}
          />
        </Space>
      ),
      flex: 1,
    },
  ];

  return (
    <Layout>
      <Container>
        <div className='content'>
          <div style={{ marginTop: 0 }}>
            <section className='admin-hero'>
              <div>
                <span className='eyebrow'>Administration</span>
                <h1>Gestion des mots de passe</h1>
                <p>
                  Réinitialisez les accès utilisateurs avec une interface claire
                  et contrôlée.
                </p>
              </div>
              <FaKey className='hero-icon' size={42} />
            </section>

            <section className='kpi-grid'>
              <KpiCard label='Utilisateurs' value={filterUsers.length || '--'} icon={<FaUsers />} />
              <KpiCard label='Comptes actifs' value={filterUsers.filter((u) => u.actif).length || '--'} icon={<FaUserCheck />} />
              <KpiCard label='Contrôle accès' value='Sécurisé' icon={<FaShieldAlt />} />
            </section>

            <div className='page-toolbar'>
              <div className='search-box'>
                <FiSearch />
              <Input
                placeholder='Rechercher...'
                onChange={(e) => {
                  setFilterUsers(
                    users.filter(
                      (u) =>
                        u.nom
                          .toLowerCase()
                          .includes(e.target.value.toLowerCase()) ||
                        u.prenom
                          .toLowerCase()
                          .includes(e.target.value.toLowerCase()) ||
                        u.agence?.nom
                          .toLowerCase()
                          .includes(e.target.value.toLowerCase()) ||
                        u.partner?.nom
                          .toLowerCase()
                          .includes(e.target.value.toLowerCase()) ||
                        u.adresse
                          .toLowerCase()
                          .includes(e.target.value.toLowerCase()),
                    ),
                  );
                }}
              />
              </div>
              <span />
              <PulseLoader
                size={5}
                margin={2}
                color={YELLOW}
                loading={isLoading}
              />
            </div>

            <div className='table-shell'>
            <Table
              columns={COLUMNS}
              dataSource={filterUsers}
              rowClassName={(row, index) =>
                index % 2 === 0 ? 'even-row' : 'odd-row'
              }
              size='small'
              style={{ zoom: 0.9 }}
              scroll={{ x: 1100 }}
              pagination={false}
              loading={isLoading}
            />
            </div>
          </div>
        </div>
        <Drawer
          title='Réinitialiser le mot de passe'
          placement='right'
          width='min(100vw, 520px)'
          onClose={() => setShowDrawer(false)}
          open={showDrawer}
          closeIcon={<MdClose size={20} />}
        >
          <PasswordForm
            user={selectedUser as UserEntity}
            setShowDrawer={setShowDrawer}
          />
        </Drawer>
      </Container>
    </Layout>
  );
};

const KpiCard = ({ icon, label, value }: { icon: ReactNode; label: string; value: ReactNode }) => (
  <div className='kpi-card'>
    <div>
      <span className='label'>{label}</span>
      <strong className='value'>{value}</strong>
    </div>
    <span className='icon'>{icon}</span>
  </div>
);
